import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2, Sparkles, ArrowLeft } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export default function PlacementTest() {
    const { testId } = useParams();
    const navigate = useNavigate();
    const [state, setState] = useState({
        loading: true,
        question: null,
        index: 0,
        total: 10,
        finished: false,
        estimated_level: null,
        breakdown: null,
        lastFeedback: null,
    });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/placement/${testId}`);
                if (data.finished) {
                    setState((s) => ({
                        ...s,
                        loading: false,
                        finished: true,
                        estimated_level: data.estimated_level,
                        breakdown: data.breakdown,
                    }));
                } else {
                    const current = data.questions?.[data.answers.length];
                    if (!current) {
                        toast.error("Impossible de reprendre ce test.");
                        return;
                    }
                    setState({
                        loading: false,
                        finished: false,
                        question: {
                            id: current.id,
                            prompt: current.prompt,
                            choices: current.choices,
                            skill: current.skill,
                        },
                        index: data.answers.length,
                        total: 10,
                        estimated_level: null,
                        breakdown: null,
                        lastFeedback: null,
                    });
                }
            } catch (err) {
                toast.error(formatApiError(err));
                setState((s) => ({ ...s, loading: false }));
            }
        })();
    }, [testId]);

    async function answer(choiceIndex) {
        if (submitting || !state.question) return;
        setSubmitting(true);
        try {
            const { data } = await api.post(`/placement/answer`, {
                test_id: testId,
                question_id: state.question.id,
                choice_index: choiceIndex,
            });
            const feedback = data.last_question
                ? {
                      correct_index: data.last_question.correct_index,
                      your_index: data.last_question.your_index,
                      was_correct: data.last_question.was_correct,
                      explanation_fr: data.last_question.explanation_fr,
                  }
                : null;
            if (data.finished) {
                setState((s) => ({
                    ...s,
                    finished: true,
                    estimated_level: data.estimated_level,
                    breakdown: data.breakdown,
                    lastFeedback: feedback,
                }));
            } else {
                setState((s) => ({
                    ...s,
                    question: data.question,
                    index: data.index,
                    lastFeedback: feedback,
                }));
            }
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    }

    async function applyLevelAndReturn() {
        try {
            const profileResp = await api.get("/profile/me");
            const current = profileResp.data.profile || {};
            await api.post("/profile/save", {
                ...current,
                level: state.estimated_level || "A1",
                placement_test_id: testId,
                completed_steps: Array.from(
                    new Set([...(current.completed_steps || []), "niveau"]),
                ),
            });
            navigate("/onboarding", { replace: true });
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }

    if (state.loading) {
        return (
            <div className="min-h-screen bg-paper flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-terracotta" />
            </div>
        );
    }

    if (state.finished) {
        return (
            <div className="min-h-screen bg-paper" data-testid="placement-result">
                <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                    <p className="text-xs tracking-[0.28em] uppercase text-inkSoft mb-4">
                        Test de placement · terminé
                    </p>
                    <h1 className="font-serif text-4xl md:text-5xl tracking-tight" data-testid="placement-title">
                        Niveau estimé :{" "}
                        <span className="text-terracotta" data-testid="estimated-level">
                            {state.estimated_level}
                        </span>
                    </h1>
                    <p className="mt-4 text-sm text-inkSoft leading-relaxed">
                        Cette estimation est <strong>indicative</strong> et ne constitue{" "}
                        <strong>pas une certification officielle</strong>. Nous l&apos;utilisons pour
                        calibrer votre programme.
                    </p>

                    {state.breakdown?.per_skill && (
                        <div className="mt-8 bg-white border border-lexiBorder rounded-2xl p-6" data-testid="placement-breakdown">
                            <p className="text-xs tracking-[0.22em] uppercase text-inkSoft mb-4">
                                Détail par compétence
                            </p>
                            <div className="space-y-3">
                                {Object.entries(state.breakdown.per_skill).map(([skill, s]) => (
                                    <div key={skill} data-testid={`breakdown-${skill}`}>
                                        <div className="flex justify-between text-sm">
                                            <span className="capitalize">{skill}</span>
                                            <span className="font-mono text-inkSoft">
                                                {s.correct}/{s.total}
                                            </span>
                                        </div>
                                        <div className="h-1.5 rounded-full bg-paperAlt overflow-hidden mt-1">
                                            <div
                                                className="h-full bg-emerald"
                                                style={{ width: `${(s.correct / Math.max(s.total, 1)) * 100}%` }}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <button
                        onClick={applyLevelAndReturn}
                        data-testid="apply-level-btn"
                        className="mt-8 inline-flex items-center gap-2 bg-terracotta hover:bg-terracottaDark text-white rounded-full px-6 py-3 text-sm font-medium active:scale-[0.98] transition-transform"
                    >
                        <Sparkles className="w-4 h-4" />
                        Utiliser ce niveau et continuer
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-paper" data-testid="placement-page">
            <header className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex justify-between items-center">
                <button
                    onClick={() => navigate("/onboarding")}
                    className="text-inkSoft hover:text-ink text-sm inline-flex items-center gap-1"
                    data-testid="placement-back"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Retour à l&apos;onboarding
                </button>
                <span className="text-xs text-inkSoft" data-testid="placement-index">
                    Question {state.index + 1} / {state.total}
                </span>
            </header>

            <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
                <div className="h-1 bg-lexiBorder rounded-full overflow-hidden mb-10">
                    <div
                        className="h-full bg-terracotta transition-all duration-500 ease-out"
                        style={{ width: `${((state.index + 1) / state.total) * 100}%` }}
                    />
                </div>

                {state.lastFeedback && (
                    <FeedbackBar feedback={state.lastFeedback} />
                )}

                <div className="bg-white border border-lexiBorder rounded-2xl p-8 md:p-10 animate-fade-up" data-testid="placement-question">
                    <p className="text-[11px] tracking-[0.22em] uppercase text-inkSoft mb-4">
                        {state.question.skill}
                    </p>
                    <p
                        className="font-serif text-xl md:text-2xl leading-snug whitespace-pre-line"
                        data-testid="question-prompt"
                    >
                        {state.question.prompt}
                    </p>

                    <div className="mt-8 space-y-2">
                        {state.question.choices.map((c, i) => (
                            <button
                                key={i}
                                onClick={() => answer(i)}
                                disabled={submitting}
                                data-testid={`choice-${i}`}
                                className="w-full text-left px-4 py-3 rounded-xl border border-lexiBorder bg-paper hover:bg-paperAlt disabled:opacity-60 transition-colors active:scale-[0.99]"
                            >
                                <span className="font-mono text-xs text-inkSoft mr-3">
                                    {String.fromCharCode(65 + i)}
                                </span>
                                <span className="text-base">{c}</span>
                            </button>
                        ))}
                    </div>

                    <button
                        onClick={() => answer(-1)}
                        disabled={submitting}
                        data-testid="skip-btn"
                        className="mt-6 text-xs text-inkSoft hover:text-ink underline underline-offset-4 disabled:opacity-60"
                    >
                        Je ne sais pas, passer
                    </button>
                </div>
            </div>
        </div>
    );
}

function FeedbackBar({ feedback }) {
    return (
        <div
            className={`mb-4 rounded-xl px-4 py-3 border ${
                feedback.was_correct
                    ? "bg-emerald/10 border-emerald/30"
                    : "bg-terracotta/8 border-terracotta/30"
            }`}
            data-testid="last-feedback"
        >
            <div className="flex items-center gap-2">
                {feedback.was_correct ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald" />
                ) : (
                    <XCircle className="w-4 h-4 text-terracotta" />
                )}
                <span className="text-sm font-medium">
                    {feedback.was_correct ? "Bien vu." : "Presque —"}
                </span>
            </div>
            {feedback.explanation_fr && (
                <p className="text-xs text-inkSoft mt-1 leading-relaxed">{feedback.explanation_fr}</p>
            )}
        </div>
    );
}
